// Central HTTP client for the NEW Laravel API (/api/*).
//
// During the PHP→Laravel migration this is introduced first; nothing is forced
// onto it yet. As each endpoint group is migrated, its frontend service module
// is repointed from the legacy `https://…/backend/*.php` URLs to this client
// (e.g. http.post('/api/login')). Legacy calls keep working until repointed.
//
// Base URL comes from VUE_APP_API_BASE (Vue CLI env var). Default keeps the
// current host so nothing breaks before an env file is set in deployment.
import axios from 'axios'

const baseURL = process.env.VUE_APP_API_BASE || 'https://m4a.ramacsystems.com'

const http = axios.create({
  baseURL,
  headers: { Accept: 'application/json' },
})

// Attach the Sanctum bearer token (set at login) to every request.
http.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// On 401 (unauthenticated / expired) clear the session so the app falls back to
// the login screen. A 403 means the user IS authenticated but lacks the role for
// that endpoint (e.g. a Manager hitting an Admin-only screen) — do NOT log them
// out; let the calling component handle the rejection.
http.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error && error.response && error.response.status
    if (status === 401) {
      localStorage.removeItem('access_token')
      localStorage.removeItem('userType')
      localStorage.removeItem('fullname')
      // Also clear the Vuex token so the app falls back to the login screen
      // REACTIVELY. Without this, state.token (read once at boot) still holds the
      // stale token, so loggedIn stays true and the dashboard renders empty until
      // the user manually refreshes. Lazy require avoids a circular import
      // (store/index -> auth -> http -> store); the store is fully initialised by
      // the time a response can arrive.
      try {
        const store = require('../store').default
        if (store) store.commit('destroyToken')
      } catch (e) { /* store not ready — localStorage clear still takes effect on next boot */ }
    }
    return Promise.reject(error)
  },
)

export default http
